// src/lib/studio.js
// Talking to the Studio service (a separate app at REACT_APP_STUDIO_URL, with
// its own database and its own session) from inside Karvi.
//
// Studio identifies callers by a BetterAuth session cookie, not by Karvi's
// token, and the two accounts are linked by email inside Studio's SSO bridge.
// A user who has logged into Karvi but never opened Studio therefore has no
// Studio session at all — so before reading anything we ask Studio to
// establish one, handing over the Karvi token exactly as the editor embed
// already does.
//
// Every call is credentialed (`credentials: "include"`): the cookie is what
// authenticates us, and both hosts sit under karviai.com, so it is same-site.

import { getToken } from "../api";

const STUDIO_URL = (process.env.REACT_APP_STUDIO_URL || "").replace(/\/$/, "");

export class StudioError extends Error {
  constructor(message, { status = 0, kind = "failed" } = {}) {
    super(message);
    this.name = "StudioError";
    this.status = status;
    this.kind = kind; // "unconfigured" | "unauthenticated" | "failed"
  }
}

// One in-flight priming request shared by every caller, so a page with several
// Studio-backed sections doesn't mint several sessions in parallel.
let sessionPromise = null;

/**
 * Make sure this browser has a Studio session, creating one from the Karvi
 * token if needed. Safe to call repeatedly: Studio answers `created: false`
 * when a valid session already exists and leaves it untouched.
 */
export function ensureStudioSession({ force = false } = {}) {
  if (force) sessionPromise = null;
  if (sessionPromise) return sessionPromise;

  sessionPromise = (async () => {
    if (!STUDIO_URL) {
      throw new StudioError("REACT_APP_STUDIO_URL is not set", { kind: "unconfigured" });
    }
    const token = getToken();
    if (!token) {
      throw new StudioError("Not signed in to Karvi", { kind: "unauthenticated" });
    }

    const res = await fetch(`${STUDIO_URL}/sso/session`, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({}),
    });

    if (!res.ok) {
      const kind = res.status === 401 ? "unauthenticated" : "failed";
      throw new StudioError(`Studio session failed (${res.status})`, { status: res.status, kind });
    }
    return res.json();
  })();

  // A failed attempt must not be cached, or the row can never recover without
  // a reload.
  sessionPromise.catch(() => {
    sessionPromise = null;
  });

  return sessionPromise;
}

async function studioGet(path) {
  const res = await fetch(`${STUDIO_URL}/backend${path}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) {
    const kind = res.status === 401 ? "unauthenticated" : "failed";
    throw new StudioError(`Studio ${path} failed (${res.status})`, { status: res.status, kind });
  }
  return res.json();
}

/**
 * The user's Studio projects, newest edit first — Studio orders by
 * `updated_at DESC`, which is exactly what "Continue editing" wants.
 *
 * Primes the session first, and retries once if the session turns out to have
 * expired between priming and reading.
 */
export async function listProjects({ limit = 6, offset = 0 } = {}) {
  await ensureStudioSession();
  const path = `/projects?limit=${encodeURIComponent(limit)}&offset=${encodeURIComponent(offset)}`;

  try {
    return await studioGet(path);
  } catch (error) {
    if (error instanceof StudioError && error.kind === "unauthenticated") {
      await ensureStudioSession({ force: true });
      return studioGet(path);
    }
    throw error;
  }
}

export const studioProjectUrl = (id) =>
  `${STUDIO_URL}/sso?token=${encodeURIComponent(getToken() || "")}&returnTo=${encodeURIComponent(`/project/${id}`)}`;

export const studioNewProjectUrl = () =>
  `${STUDIO_URL}/sso?token=${encodeURIComponent(getToken() || "")}&returnTo=${encodeURIComponent("/projects")}`;
